import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';
import useSWR from 'swr';

import { APISource } from '../../../../../typings/Source.interface';
import { APIWebhook } from '../../../../../typings/Webhook.interface';
import APIMethodKeys from '../../../dashboard/client/APIMethodKeys';
import Button from '../../../dashboard/components/common/base/Button';
import Icon from '../../../dashboard/components/common/base/Icon';
import Text from '../../../dashboard/components/common/base/Text';
import { Div } from '../../../dashboard/components/common/helpers/StyledUtils';
import useLocalStorage from '../../../dashboard/components/hooks/useLocalStorage';
import {
  stripLegacySourceIdParam,
  getDeepLinkSourceId,
  syncDeepLinkToSource,
} from '../utils/deep-link-source-id';
import { inspectPathForSourceId, isNewSourcePath } from '../utils/new-source';
import { ConsoleGlobalContext } from './ConsoleGlobalContext';
import { ConsoleSidebarContext } from './ConsoleSidebarContext';

interface ConsoleResourceContextProps {
  source: APISource;
  webhook: APIWebhook;
  setSource: (source: APISource) => void;
  deleteSource: () => void;
  setWebhook: (webhook: APIWebhook) => void;
}

export const ConsoleResourceContext = createContext<ConsoleResourceContextProps>({
  source: {} as APISource,
  webhook: {} as APIWebhook,
  setSource: () => null,
  deleteSource: () => null,
  setWebhook: () => null,
});

export const ConsoleResourceContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { HookdeckAPI, team, context: console_context } = useContext(ConsoleGlobalContext);
  const { closeSidebar } = useContext(ConsoleSidebarContext);
  const [source, setSource] = useLocalStorage<APISource | null>(
    `${team.id}:console:state:source`,
    null,
  );
  const [webhook, setWebhook] = useLocalStorage<APIWebhook | null>(
    `${team.id}:console:state:webhook`,
    null,
  );

  // The deep-linked Source id (`/{id}`, `/e/{id}`, or the legacy `?source_id=`). Read once
  // and kept for the provider's lifetime: it decides how this visit resolves a Source, and
  // it must not be dropped mid-flight the way it used to be — losing it is what made a
  // shared inspect link silently fall back to the visitor's own sandbox.
  const [deep_link_source_id] = useState<string | null>(() => getDeepLinkSourceId());
  // How far the deep link has got. `resolved` hands control back to the normal
  // default-Source resolution (the user is free to switch Sources from here); `failed` is
  // terminal and must stay sticky — the SWR error clears the moment its key goes away, so
  // a flag derived from the fetch alone would let the fallback create fire anyway.
  const [deep_link_state, setDeepLinkState] = useState<'pending' | 'resolved' | 'failed'>(
    'pending',
  );

  const is_new_source_request = isNewSourcePath(window.location.pathname);
  const deep_link_failed = !!deep_link_source_id && deep_link_state === 'failed';
  const pending_deep_link_id =
    deep_link_source_id && deep_link_state === 'pending' && !is_new_source_request
      ? deep_link_source_id
      : null;

  useEffect(() => {
    stripLegacySourceIdParam();
  }, []);

  // `/new` always provisions a fresh Source and redirects to its inspect path
  // (browser-only; programmatic callers use POST /). Honours the optional, lenient
  // `?type=` query param. A duplicate default name returns 409, so retry with a
  // suffix; an unknown type returns 422, so retry without it (lenient).
  useEffect(() => {
    if (!is_new_source_request) {
      return;
    }
    const pinned_type = new URLSearchParams(window.location.search).get('type') || undefined;
    let cancelled = false;
    HookdeckAPI.sources
      .create({ name: 'Source', type: pinned_type })
      .catch((error) => {
        const status = error?.response?.status;
        if (status === 409) {
          return HookdeckAPI.sources.create({
            name: `Source ${Math.random().toString(36).slice(2, 6)}`,
            type: pinned_type,
          });
        }
        // Lenient: an unknown/invalid `?type=` shouldn't block /new — drop it.
        if (status === 422 && pinned_type) {
          return HookdeckAPI.sources.create({ name: 'Source' });
        }
        throw error;
      })
      .then((created) => {
        if (!cancelled && created) {
          window.location.replace(inspectPathForSourceId(created.id));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [HookdeckAPI, is_new_source_request]);

  // Deep link: resolve the Source by id. `sources.get` is used rather than
  // `sources.list({ id })` so "this Source isn't visible to you" is a 404 we can render,
  // instead of an empty list that is indistinguishable from "this sandbox has no Sources"
  // — the ambiguity that had a broken share link quietly create an unrelated Source.
  const { data: deep_link_source, error: deep_link_error } = useSWR(
    pending_deep_link_id && APIMethodKeys.sources.get(pending_deep_link_id),
    () => HookdeckAPI.sources.get(pending_deep_link_id!),
    { shouldRetryOnError: false },
  );

  useEffect(() => {
    if (!pending_deep_link_id) {
      return;
    }
    if (deep_link_source) {
      setSource(deep_link_source);
      setWebhook(null);
      setDeepLinkState('resolved');
    } else if (deep_link_error) {
      setDeepLinkState('failed');
    }
  }, [deep_link_source, deep_link_error, pending_deep_link_id]);

  // Default Source resolution, for a visit that isn't following a deep link.
  const { data: sources, mutate } = useSWR(
    !pending_deep_link_id &&
      !deep_link_failed &&
      APIMethodKeys.sources.list({ limit: 1, id: source?.id || undefined }),
    () => HookdeckAPI.sources.list({ limit: 1, id: source?.id || undefined }),
  );

  // If no source, create a default one (skipped on /new and while a deep link is pending)
  useEffect(() => {
    if (is_new_source_request || pending_deep_link_id || deep_link_failed) {
      return;
    }
    if (!source && sources?.models?.length === 0) {
      HookdeckAPI.sources
        .create({ name: 'Source' })
        .catch((error) => {
          if (error?.response?.status === 409) {
            return HookdeckAPI.sources.enable(error.response.data.source.id);
          }
          throw error;
        })
        .then((source) => {
          mutate({ ...sources, models: [source] }, false);
          setSource(source);
        });
    } else {
      if (sources?.models?.length) {
        const default_source = sources && sources.models.length > 0 ? sources.models[0] : null;
        setSource(default_source);
      }
      if (source && sources?.models?.length === 0) {
        setSource(null);
      }
    }
  }, [sources, HookdeckAPI, mutate, pending_deep_link_id, deep_link_failed]);

  // Keep the address bar on the Source that's on screen, so a reload, a bookmark or a
  // copy-pasted URL all come back to the same place.
  //
  // `fullpage` only. The embed and inline builds mount inside a third-party page, where the
  // address bar belongs to the host site - rewriting it there would change their URL and
  // point a refresh at a route that does not exist on their domain.
  //
  // And not while the deep link is still resolving, or after it has failed. `source` is
  // restored from localStorage, so on a shared link it holds the visitor's previously
  // viewed Source from the very first render. Syncing then would overwrite the shared id
  // in the address bar before it resolves, leave a failed link showing the cached Source's
  // URL, and send a refresh to that Source instead - undoing the fix this branch exists
  // for. Once the deep link resolves, the id it wrote is the one on screen, so the sync
  // is a no-op until the user switches Source themselves.
  useEffect(() => {
    if (console_context !== 'fullpage' || pending_deep_link_id || deep_link_failed) {
      return;
    }
    if (source?.id && !is_new_source_request) {
      syncDeepLinkToSource(source.id);
    }
  }, [source?.id, is_new_source_request, console_context, pending_deep_link_id, deep_link_failed]);

  const { data: webhooks } = useSWR(
    source && APIMethodKeys.webhooks.list({ limit: 1, source_id: source.id }),
    () => HookdeckAPI.webhooks.list({ limit: 1, source_id: source?.id }),
  );

  useEffect(() => {
    const default_webhook = webhooks && webhooks.models.length > 0 ? webhooks.models[0] : null;
    if (!webhook) {
      setWebhook(default_webhook);
    }
  }, [webhooks]);

  const context: ConsoleResourceContextProps = {
    source: source || ({} as APISource),
    webhook: webhook || ({} as APIWebhook),
    setWebhook: setWebhook,
    deleteSource: () => {
      setSource(null);
      setWebhook(null);
      closeSidebar();
    },
    setSource: (new_source) => {
      if (source?.id !== new_source.id) {
        setWebhook(null);
      }
      setSource(new_source);
    },
  };

  // A deep link that resolved to nothing is terminal: say so, rather than falling through
  // to the visitor's own sandbox and showing them a different Source under the URL they
  // were given.
  if (deep_link_failed) {
    return (
      <Div
        h={100}
        p={{ all: 8 }}
        flex={{ direction: 'column', align: 'center', justify: 'center' }}>
        <Icon icon="warning" muted />
        <Text as="h2" bold size="xl" m={{ t: 3, b: 3 }}>
          This test URL isn&apos;t available
        </Text>
        <Text as="p" size="m" center m={{ b: 6 }}>
          It may have been deleted, or the link may be incomplete. A test URL is only reachable with
          its full, exact link.
        </Text>
        <Button primary onClick={() => window.location.replace('/new')}>
          Create a new test URL
        </Button>
      </Div>
    );
  }

  if (pending_deep_link_id && !deep_link_source) {
    return (
      <Div h={100} flex={{ justify: 'center', align: 'center' }}>
        <Icon icon="loading" muted />
      </Div>
    );
  }

  return (
    <ConsoleResourceContext.Provider value={context}>{children}</ConsoleResourceContext.Provider>
  );
};
